import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  Slider,
  Tabs,
  Tab,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import StoreHeader from '../../components/layout/StoreHeader';
import StoreMobileActionBar, { MOBILE_ACTION_BAR_PAD } from '../../components/layout/StoreMobileActionBar';
import CardCoverImage from '../../components/CardCoverImage';
import CardQuickViewModal from '../../components/CardQuickViewModal';
import HowItWorksModal from '../../components/HowItWorksModal';
import InviteBand from '../../components/store/InviteBand';
import SavedDesignsRow from '../../components/store/SavedDesignsRow';
import { getCreations } from '../../lib/myCreations';
import apiClient from '../../lib/api';

// Format date-only (YYYY-MM-DD) as calendar date to avoid timezone shifting the day
const formatDateOfDeath = (dateString) => {
  if (!dateString) return '';
  const isoDate = String(dateString).split('T')[0];
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Mock data for development
const mockProducts = [
  {
    id: '1',
    name: 'Religious Sympathy Card - God\'s Love',
    price: 4.99,
    originalPrice: 6.99,
    image: '/images/store/sympathy-card-1.jpg',
    category: 'religious',
    occasion: 'funeral',
    style: 'traditional',
    inStock: true,
    isFavorite: false,
  },
  {
    id: '2',
    name: 'Secular Memorial Card - In Loving Memory',
    price: 5.99,
    originalPrice: 7.99,
    image: '/images/store/memorial-card-1.jpg',
    category: 'secular',
    occasion: 'funeral',
    style: 'modern',
    inStock: true,
    isFavorite: false,
  },
  {
    id: '3',
    name: 'Children\'s Sympathy Card - Gentle Hugs',
    price: 3.99,
    originalPrice: 5.99,
    image: '/images/store/condolence-card-1.jpg',
    category: 'children',
    occasion: 'funeral',
    style: 'traditional',
    inStock: true,
    isFavorite: false,
  },
  {
    id: '4',
    name: 'Veteran Memorial Card - Service & Honor',
    price: 6.99,
    originalPrice: 8.99,
    image: '/images/store/funeral-program-1.jpg',
    category: 'veterans',
    occasion: 'funeral',
    style: 'elegant',
    inStock: true,
    isFavorite: false,
  },
  {
    id: '5',
    name: 'Personalized Sympathy Card - Custom Message',
    price: 8.99,
    originalPrice: 10.99,
    image: '/images/store/personalized-card-1.jpg',
    category: 'personalized',
    occasion: 'funeral',
    style: 'modern',
    inStock: true,
    isFavorite: false,
  },
  {
    id: '6',
    name: 'Condolence Message Card - With Love',
    price: 4.49,
    originalPrice: 5.99,
    image: '/images/store/condolence-message-1.jpg',
    category: 'condolence',
    occasion: 'general',
    style: 'simple',
    inStock: true,
    isFavorite: false,
  },
];


const occasions = [
  { id: 'funeral', name: 'Funeral' },
  { id: 'memorial', name: 'Memorial Service' },
  { id: 'anniversary', name: 'Death Anniversary' },
  { id: 'general', name: 'General Sympathy' },
];

const styles = [
  { id: 'traditional', name: 'Traditional' },
  { id: 'modern', name: 'Modern' },
  { id: 'elegant', name: 'Elegant' },
  { id: 'simple', name: 'Simple' },
];

const ITEMS_PER_PAGE = 20; // Number of cards to load per page

// On the home browse view, keep pulling pages on initial load until we have enough of the catalog
// to populate rails meaningfully — so the rails shown aren't dependent on a single lucky page
// (perf 0b). Infinite scroll continues past this once the user scrolls.
const BROWSE_MIN_CARDS = 60;

// Initial home load fires this many card-list pages CONCURRENTLY (perf phase 3 / audit 2026-06-26):
// serially awaiting page 1→2→3 was the longest pole (~3.3s). They multiplex over h2/h3, so firing
// them at once collapses that to ~one page's latency. Infinite scroll resumes past this set.
const INITIAL_BROWSE_PAGES = Math.max(1, Math.ceil(BROWSE_MIN_CARDS / ITEMS_PER_PAGE));

const STORE_CATALOG_SEED_KEY = 'imo_store_catalog_seed';

/** Per browser tab session: stable order for infinite scroll; new session => new shuffle. */
function getOrCreateSessionCatalogSeed() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    let seed = sessionStorage.getItem(STORE_CATALOG_SEED_KEY);
    if (!seed || seed.length > 128) {
      const arr = new Uint8Array(16);
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(arr);
      } else {
        for (let i = 0; i < arr.length; i += 1) arr[i] = Math.floor(Math.random() * 256);
      }
      seed = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem(STORE_CATALOG_SEED_KEY, seed);
    }
    return seed;
  } catch {
    return null;
  }
}

// Browse taxonomy — fallback only. The live, ordered set is fetched from GET /api/taxonomy so the
// taxonomy can evolve without a frontend deploy; this constant keeps the home working if that
// read fails. Rails render only for categories the loaded cards are actually assigned to.
const DEFAULT_STORE_CATEGORIES = [
  { id: 'when-words-are-hard', name: 'When Words Are Hard' },
  { id: 'simple-sincere', name: 'Simple & Sincere' },
  { id: 'faith-hope', name: 'Faith & Hope' },
  { id: 'love-remembrance', name: 'Love & Remembrance' },
  { id: 'strength-encouragement', name: 'Strength & Encouragement' },
  { id: 'thinking-of-you', name: 'Thinking of You' },
  { id: 'celebration-of-life', name: 'Celebration of Life' },
  { id: 'humor', name: 'Humor' },
  { id: 'the-lighter-side', name: 'The Lighter Side' },
  { id: 'a-hug-in-an-envelope', name: 'A Hug in an Envelope' },
  { id: 'loss-of-a-parent', name: 'Loss of a Parent' },
  { id: 'loss-of-a-spouse-or-partner', name: 'Loss of a Spouse or Partner' },
  { id: 'loss-of-a-child', name: 'Loss of a Child' },
  { id: 'loss-of-a-sibling', name: 'Loss of a Sibling' },
  { id: 'loss-of-a-grandparent', name: 'Loss of a Grandparent' },
  { id: 'loss-of-a-friend', name: 'Loss of a Friend' },
  { id: 'honor-their-service', name: 'Honor Their Service' },
  { id: 'nature-reflection', name: 'Nature & Reflection' },
  { id: 'artistic-contemporary', name: 'Artistic & Contemporary' },
  { id: 'religious', name: 'Religious' },
  { id: 'traditional', name: 'Traditional' },
  { id: 'personal', name: 'Personal' },
  { id: 'cultural', name: 'Cultural' },
  { id: 'uplifting', name: 'Uplifting' },
];

// Deterministic 32-bit string hash (FNV-1a) → 8-hex string, for seeded ordering comparisons.
function hashKey(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// The taxonomy ids a product is assigned to (structured multi-label categories from the backend).
export function categoryIdsForProduct(product) {
  if (!product || !Array.isArray(product.categories)) return [];
  return product.categories
    .map((c) => (typeof c === 'string' ? c : c && c.id))
    .filter(Boolean);
}

// Seeded ordering: deterministic for a given seed (stable within a session / across paged loads —
// no dupes or skips on infinite scroll), independent of input order, and different for a different
// seed. Mirrors the backend orderCardsForListing seed-sort (BR-CAT-08).
export function seededShuffle(items, seedStr) {
  return (items || [])
    .map((item) => ({ item, key: hashKey(`${seedStr}:${item.id}`) }))
    .sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : String(a.item.id).localeCompare(String(b.item.id))
    )
    .map((d) => d.item);
}

// Build one rail per non-empty category from card.categories (no tag/style fallback). A multi-label
// card appears in every matching rail; an uncategorized card appears in none. Each rail is shuffled
// independently with its own per-visit seed.
export function buildCategoryRails(products, taxonomy, seed) {
  return (taxonomy || [])
    .map((category) => {
      const items = (products || []).filter((p) =>
        categoryIdsForProduct(p).includes(category.id)
      );
      return { category, items: seededShuffle(items, `${seed || ''}:${category.id}`) };
    })
    .filter((rail) => rail.items.length > 0);
}

// Single fallback hero used when the banner manifest is missing/empty so the hero never breaks.
const HERO_FALLBACK_SRC = `${process.env.PUBLIC_URL}/images/hero.jpeg`;

// Fisher–Yates in-place shuffle (returns the same array for convenience).
function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Rotating gallery of finished landscape banners (logo + headline baked in). Banners are shown
// WHOLE (object-fit: contain) on a dark backdrop — never cropped, no text overlay. Auto-advances
// with a cross-fade; honors prefers-reduced-motion; falls back to a single hero image on failure.
const HeroCarousel = () => {
  const [slides, setSlides] = useState(null); // null = loading
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const reducedMotion = useRef(prefersReducedMotion());
  const touchStartRef = useRef(null); // { x, y } of the active touch start

  useEffect(() => {
    let cancelled = false;
    const base = `${process.env.PUBLIC_URL}/images/hero`;

    const load = async () => {
      try {
        if (typeof fetch !== 'function') throw new Error('fetch unavailable');
        const res = await fetch(`${base}/manifest.json`);
        if (!res.ok) throw new Error(`manifest ${res.status}`);
        const names = await res.json();
        if (cancelled) return;
        if (Array.isArray(names) && names.length > 0) {
          const ordered = shuffleInPlace([...names]);
          setSlides(ordered.map((name) => `${base}/${encodeURIComponent(name)}`));
        } else {
          setSlides([HERO_FALLBACK_SRC]);
        }
      } catch (error) {
        if (!cancelled) setSlides([HERO_FALLBACK_SRC]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const autoAdvancing =
    !!slides && slides.length > 1 && !reducedMotion.current && playing;

  useEffect(() => {
    if (!autoAdvancing) return undefined;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [autoAdvancing, slides]);

  const go = (direction) => {
    if (!slides || slides.length === 0) return;
    setCurrent((c) => (c + direction + slides.length) % slides.length);
  };

  // Mobile swipe-to-advance. We only MEASURE the gesture (no preventDefault on touchmove), so vertical
  // page scrolling is never blocked; the direction is decided once, at touchEnd.
  const SWIPE_THRESHOLD = 40; // px of horizontal travel required to count as a swipe
  const handleTouchStart = (e) => {
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || !slides || slides.length <= 1) return;
    const touch = e.changedTouches && e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    // Require horizontal intent: past the threshold AND more horizontal than vertical (not a scroll).
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1); // swipe LEFT → next, swipe RIGHT → prev
    }
  };

  // Reserve the hero space (dark backdrop) while the manifest loads.
  if (!slides) {
    return (
      <Box
        aria-hidden
        sx={{ width: '100%', aspectRatio: '16 / 9', backgroundColor: '#1f2a37' }}
      />
    );
  }

  const showControls = slides.length > 1;

  return (
    <Box
      component="section"
      aria-label="The Card Store image carousel"
      data-testid="hero-carousel"
      data-autoplay={String(autoAdvancing)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#1f2a37',
        aspectRatio: '16 / 9',
      }}
    >
      {slides.map((src, idx) => (
        <Box
          key={src}
          component="img"
          src={src}
          alt=""
          data-testid="hero-slide"
          loading={idx === current ? 'eager' : 'lazy'}
          style={{ objectFit: 'contain' }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: idx === current ? 1 : 0,
            transition: 'opacity 1.1s ease',
          }}
        />
      ))}

      {showControls && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 10,
            transform: 'translateX(-50%)',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'rgba(20,28,36,0.5)',
            borderRadius: '24px',
            p: 0.5,
          }}
        >
          <IconButton aria-label="Previous image" onClick={() => go(-1)} size="small" sx={{ color: '#fff' }}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
            aria-pressed={!playing}
            onClick={() => setPlaying((p) => !p)}
            size="small"
            sx={{ color: '#fff' }}
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <IconButton aria-label="Next image" onClick={() => go(1)} size="small" sx={{ color: '#fff' }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// Default navigation state passed to the personalize editor (preserves prior behavior).
const CARD_NAV_STATE = { stockType: 'standard', price: 14.99 };

// Image-only browse tile. Tapping it opens the rail's shared quick-view modal (enlarged cover +
// Personalize + looping arrows through the rail) rather than navigating straight to the editor — so a
// shopper can look before committing, and swipe siblings. Rendered as a real <button> for keyboard
// focus + screen readers; the modal (and its navigation) is owned by the rail so it knows the group.
const ProductCard = ({ product, onOpen }) => {
  return (
    <Card
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`Preview ${product.name}`}
      sx={{
        position: 'relative',
        display: 'block',
        width: '100%',
        padding: 0,
        border: 0,
        font: 'inherit',
        textAlign: 'inherit',
        cursor: 'pointer',
        overflow: 'hidden',
        backgroundColor: 'grey.100',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}
    >
      <CardCoverImage
        cardId={product.id}
        imageId={product.coverImageId}
        coverImageUrl={product.coverImageUrl}
        alt={product.name}
      />
      {product.originalPrice > product.price && (
        <Chip
          label="Sale"
          color="error"
          size="small"
          sx={{ position: 'absolute', top: 8, left: 8 }}
        />
      )}
    </Card>
  );
};

// "Create your own" entry tile — the LAST item in every rail (design: CardStoreApp.dc.html vRevised,
// `rail.showCreate`). It used to lead the rail as a dashed placeholder; terminal position means only
// a shopper who looked through the whole set and did not find it meets the offer — exactly the right
// audience — while position one stays merchandising. The tile is now a designed cover in its own
// right (navy, sparkle sweep, script title) rather than an empty slot, so the rail does not open on
// a hole. Opens the "how it works" modal → the create wizard.
const CreateTile = () => {
  const [howOpen, setHowOpen] = useState(false);
  return (
    <>
      <Card
        component="button"
        type="button"
        onClick={() => setHowOpen(true)}
        aria-label="Design your own card"
        data-testid="create-tile"
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio: '5 / 7',
          p: 0,
          border: 0,
          borderRadius: '8px',
          overflow: 'hidden',
          font: 'inherit',
          cursor: 'pointer',
          backgroundColor: '#0d3d50',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 6px 18px rgba(13,61,80,0.32)' },
          '@keyframes createTileSpin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          },
          '@keyframes createTileTwinkle': {
            '0%, 100%': { opacity: 0.25 },
            '50%': { opacity: 1 },
          },
        }}
      >
        {/* A slow conic sweep behind a 1.5px inset face reads as a lit edge — the "one of a kind"
            signal, with no border colour to fight the covers beside it. Decorative; stops on
            prefers-reduced-motion, where the tile simply sits still. */}
        <Box
          aria-hidden
          component="span"
          sx={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background:
              'conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0) 200deg, rgba(232,196,115,0.75) 260deg, rgba(255,255,255,0.95) 290deg, rgba(160,220,240,0.7) 320deg, rgba(255,255,255,0) 360deg)',
            animation: 'createTileSpin 5s linear infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Box
          component="span"
          sx={{
            position: 'absolute',
            inset: '1.5px',
            borderRadius: '7px',
            background:
              'radial-gradient(120% 90% at 50% 8%, #2a7f9c 0%, #175d76 42%, #0e4459 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: '12px 10px',
            overflow: 'hidden',
          }}
        >
          {[
            { top: '14%', left: '17%', color: '#f0d9a2', fontSize: 11, delay: '0s' },
            { top: '30%', right: '15%', color: '#cfe9f4', fontSize: 8, delay: '0.7s' },
            { bottom: '19%', left: '24%', color: '#f0d9a2', fontSize: 7, delay: '1.4s' },
          ].map((spark) => (
            <Box
              key={spark.delay}
              aria-hidden
              component="span"
              sx={{
                position: 'absolute',
                top: spark.top,
                bottom: spark.bottom,
                left: spark.left,
                right: spark.right,
                color: spark.color,
                fontSize: spark.fontSize,
                lineHeight: 1,
                animation: `createTileTwinkle 2.6s ease-in-out ${spark.delay} infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              ✦
            </Box>
          ))}
          <AutoAwesomeIcon
            aria-hidden
            sx={{ fontSize: { xs: 22, md: 26 }, color: '#f0d9a2', mb: 1 }}
          />
          <Typography
            component="span"
            sx={{
              fontFamily: "'Segoe Script','Segoe',cursive",
              fontSize: { xs: 14, md: 18 },
              lineHeight: 1.2,
              color: '#fff',
              letterSpacing: '0.01em',
            }}
          >
            Create your own
          </Typography>
          <Box
            aria-hidden
            component="span"
            sx={{
              width: { xs: 26, md: 34 },
              height: '1px',
              backgroundColor: 'rgba(240,217,162,0.55)',
              my: 1,
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.88)',
              lineHeight: 1.35,
            }}
          >
            One of a kind
          </Typography>
        </Box>
      </Card>
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </>
  );
};

// How many rails mount their cards immediately (above-the-fold). Rails past this are lazy-mounted as
// they approach the viewport (IntersectionObserver), bounding the initial DOM + image burst. Tests
// render only a couple of rails, so they all fall within the eager window and mount synchronously.
const EAGER_RAILS = 3;

// A single horizontally-scrolling, swipeable category rail with a heading and a "See all" link.
// Scroll chevrons are desktop-only; touch swipe is native on all sizes.
// `eager` rails render their cards on first paint; the rest defer until near the viewport.
const CategoryRail = ({ category, products, eager = false }) => {
  const navigate = useNavigate();
  const railRef = useRef(null);
  const sectionRef = useRef(null);

  // The rail OWNS the quick-view so the modal knows the ordered group + clicked index (looping).
  // `quickViewIndex` indexes into `products` (the CreateTile is not part of the gallery). null = closed.
  const [quickViewIndex, setQuickViewIndex] = useState(null);
  const quickViewOpen = quickViewIndex !== null;
  const quickViewItems = useMemo(
    () =>
      products.map((p) => ({
        cardId: p.id,
        imageId: p.coverImageId,
        coverImageUrl: p.coverImageUrl,
        name: p.name,
        aiReferenceBlocked: !!p.aiReferenceBlocked,
      })),
    [products]
  );
  const currentCard =
    quickViewOpen && quickViewItems.length ? quickViewItems[quickViewIndex % quickViewItems.length] : null;

  const handlePersonalize = () => {
    if (!currentCard) return;
    setQuickViewIndex(null);
    // Carry the canonical cover URL/id so personalize reuses the SAME cached WebP (no refetch).
    navigate(`/personalize/${currentCard.cardId}`, {
      state: {
        ...CARD_NAV_STATE,
        coverImageId: currentCard.imageId,
        coverImageUrl: currentCard.coverImageUrl,
      },
    });
  };

  // "Create one like this" (approved mockup): carry the source card into the seeded "Make it yours"
  // screen (create flow), silently preselecting its recipe + attaching it as the visual reference.
  const handleCreateLikeThis = () => {
    if (!currentCard) return;
    setQuickViewIndex(null);
    navigate(`/create?like=${encodeURIComponent(currentCard.cardId)}`, {
      state: { likeCoverImageUrl: currentCard.coverImageUrl, likeName: currentCard.name },
    });
  };
  // Mount cards immediately for eager rails; off-screen rails wait for the observer. If
  // IntersectionObserver isn't available, mount eagerly so nothing is ever stranded.
  const [mounted, setMounted] = useState(
    eager || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (mounted) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return undefined;
    }
    const el = sectionRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px' } // start loading a little before the rail scrolls into view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);

  // Scroll-aware arrows: show LEFT only once scrolled off the start, RIGHT only while more remains,
  // and NEITHER when the rail doesn't overflow. This stops the left arrow from sitting on top of the
  // first tile (every rail starts at scrollLeft 0) and the right arrow from hovering a dead end.
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const epsilon = 2; // tolerate sub-pixel rounding
    setCanScrollLeft(el.scrollLeft > epsilon);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      if (resizeObserver) resizeObserver.disconnect();
    };
    // Recompute when cards mount (overflow appears) or the product set changes.
  }, [updateScrollState, mounted, products]);

  const handleScroll = (direction) => {
    if (railRef.current) {
      railRef.current.scrollBy({ left: direction * 600, behavior: 'smooth' });
    }
  };

  return (
    <Box component="section" ref={sectionRef} data-testid={`rail-${category.id}`} sx={{ my: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1.5, md: 3 }, mb: 0.5 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1rem', md: '1.2rem' },
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          {category.name}
        </Typography>
        <Typography
          component={RouterLink}
          to={`/?tag=${category.id}`}
          sx={{
            ml: 'auto',
            color: '#206884',
            fontSize: '0.85rem',
            fontWeight: 500,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          See all ›
        </Typography>
      </Box>

      {/*
        The horizontal inset lives on THIS wrapper, NOT on the scroll container. With scroll-snap, any
        padding-left on the scroller becomes the resting scrollLeft (MEASURED 24px on every overflowing
        rail) — which (a) put scrollLeft past the canScrollLeft>2 threshold so the left arrow rendered
        over the first tile at rest, and (b) parked overflowing rails 24px right of short rails (which
        rest at 0) → "short rails look indented." Insetting the wrapper instead keeps the scroller's
        resting scrollLeft at 0 on EVERY rail, so the first tile is flush with the (equally-inset)
        section title and the left arrow is correctly absent at rest.
      */}
      <Box sx={{ position: 'relative', px: { xs: 1.5, md: 3 } }}>
        {canScrollLeft && (
          <IconButton
            aria-label={`Scroll ${category.name} left`}
            onClick={() => handleScroll(-1)}
            sx={{
              display: { xs: 'none', md: 'grid' },
              position: 'absolute',
              left: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              boxShadow: 1,
              '&:hover': { backgroundColor: '#fff' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Box
          ref={railRef}
          sx={{
            display: 'flex',
            gap: { xs: 1.25, md: 2 },
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            // NO horizontal padding here — see the wrapper comment above (scroll-snap parks scrollLeft
            // on padding-left). The inset is on the wrapper so resting scrollLeft is 0 on every rail.
            px: 0,
            py: 1,
            // Reserve height before an off-screen rail mounts so the page layout stays stable.
            minHeight: mounted ? undefined : { xs: 200, md: 260 },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {mounted &&
            products.map((product, productIndex) => (
              <Box
                key={product.id}
                sx={{
                  flex: '0 0 auto',
                  width: { xs: '28vw', sm: 150, md: 160 },
                  maxWidth: { xs: 150, sm: 160, md: 180 },
                  scrollSnapAlign: 'start',
                }}
              >
                <ProductCard product={product} onOpen={() => setQuickViewIndex(productIndex)} />
              </Box>
            ))}
          {/* Always the LAST item, after the seeded-shuffled product cards — never shuffled into
              them. It is the offer made to whoever reached the end of the set without finding it. */}
          <Box
            sx={{
              flex: '0 0 auto',
              width: { xs: '28vw', sm: 150, md: 160 },
              maxWidth: { xs: 150, sm: 160, md: 180 },
              scrollSnapAlign: 'start',
            }}
          >
            <CreateTile />
          </Box>
        </Box>

        {canScrollRight && (
          <IconButton
            aria-label={`Scroll ${category.name} right`}
            onClick={() => handleScroll(1)}
            sx={{
              display: { xs: 'none', md: 'grid' },
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              boxShadow: 1,
              '&:hover': { backgroundColor: '#fff' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {/* One shared quick-view for the whole rail: looping arrows cycle the rail's cards; the count is
          omitted (a rail is a SLICE of the category) in favor of a "See all in {Category}" link. */}
      <CardQuickViewModal
        open={quickViewOpen}
        onClose={() => setQuickViewIndex(null)}
        items={quickViewItems}
        index={quickViewIndex ?? 0}
        onIndexChange={setQuickViewIndex}
        alt={currentCard ? currentCard.name : ''}
        ariaLabel={currentCard ? `${currentCard.name} preview` : 'Card preview'}
        {...(currentCard?.aiReferenceBlocked
          ? {}
          : { primaryAction: { label: 'Create one like this', onClick: handleCreateLikeThis } })}
        actionLabel="Use this exact card"
        onConfirm={handlePersonalize}
        seeAll={{ label: `See all in ${category.name}`, href: `/?tag=${category.id}` }}
      />
    </Box>
  );
};

const StorePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false); // Prevent duplicate calls
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [cartCount, setCartCount] = useState(0);
  const [obituary, setObituary] = useState(null);
  const [loadingObituary, setLoadingObituary] = useState(false);
  // Live category taxonomy from GET /api/taxonomy; the constant is only a resilience fallback.
  const [taxonomy, setTaxonomy] = useState(DEFAULT_STORE_CATEGORIES);
  const observerTarget = useRef(null);
  const loadingCardsRef = useRef(false); // Prevent duplicate loadCards calls
  const [quickViewCard, setQuickViewCard] = useState(null);

  // Helper function to map cards to products format
  const mapCardsToProducts = useCallback((cards) => {
    return cards
      .filter(card => card.status === 'published') // Only show published cards
      .map(card => ({
        id: card.id,
        name: card.title || 'Untitled Card',
        price: card.price || 14.99,
        originalPrice: card.originalPrice || card.price || 14.99,
        // Cover comes INLINE from the card-list payload now (store-home perf, audit 2026-06-26):
        // `coverImageUrl` is a directly-fetchable WebP preview URL (CDN or presigned), `coverImageId`
        // backs the source→placeholder fallback. No per-card image round-trips. <CardCoverImage>
        // renders it and degrades to a real placeholder when absent.
        coverImageId: card.coverImageId || null,
        coverImageUrl: card.coverImageUrl || null,
        category: card.category?.toLowerCase() || 'general',
        // Structured multi-label category assignments (BR-CAT-05) — rails are built from these.
        categories: Array.isArray(card.categories) ? card.categories : [],
        tags: card.tags || [], // Include tags for filtering
        aiReferenceBlocked: !!card.aiReferenceBlocked,
        occasion: 'funeral', // Default occasion
        // No 'traditional' fallback: an unclassified card joins no rail (it was the one-row bug).
        style: card.category?.toLowerCase() || '',
        inStock: true, // Cards are always "in stock" if published
        isFavorite: false,
        description: card.description || '',
        status: card.status || 'draft',
      }));
  }, []);

  // Load cards from API with pagination
  const loadCards = useCallback(async (pageNum = 1, append = false, tagFilter = null) => {
    // Prevent duplicate calls
    if (loadingCardsRef.current) {
      console.log('Skipping duplicate loadCards call for page', pageNum);
      return;
    }
    
    loadingCardsRef.current = true;
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const requestParams = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        status: 'published',
      };

      const catalogSeed = getOrCreateSessionCatalogSeed();
      if (catalogSeed) {
        requestParams.seed = catalogSeed;
      }
      
      // Add tag filter if provided (explicitly check for null/undefined, not falsy)
      const tagToUse = tagFilter !== null && tagFilter !== undefined ? tagFilter : selectedTag;
      console.log('Tag filter value:', tagFilter, 'selectedTag:', selectedTag, 'tagToUse:', tagToUse);
      if (tagToUse && tagToUse.trim() !== '') {
        requestParams.tag = tagToUse;
        console.log('Added tag to requestParams:', requestParams);
      } else {
        console.log('No tag to add to requestParams');
      }
      
      console.log('Loading cards with params:', requestParams);
      const response = await apiClient.getCards(requestParams);
      
      console.log("RESPONSE", response);
      
      if (response.success) {
        const rawCards = response.data || [];

        // Render from the card-list response immediately. Covers are now INLINE in the payload
        // (coverImageUrl/coverImageId) — no per-card image round-trips — so cards paint with their
        // cover (or a placeholder) in one pass.
        const mappedProducts = mapCardsToProducts(rawCards);

        if (append) {
          setProducts(prev => [...prev, ...mappedProducts]);
        } else {
          setProducts(mappedProducts);
        }

        // Update pagination state
        if (response.pagination) {
          setHasMore(response.pagination.hasNext || false);
          setPage(pageNum);
        } else {
          // Fallback: if no pagination info, assume no more if we got fewer items than requested
          setHasMore(mappedProducts.length === ITEMS_PER_PAGE);
        }
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingCardsRef.current = false;
    }
  }, [mapCardsToProducts, selectedTag]);

  // Load more cards (for infinite scroll)
  const loadMoreCards = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadCards(page + 1, true, selectedTag); // Pass current tag for pagination
    }
  }, [page, loadingMore, hasMore, selectedTag, loadCards]);

  // Initial HOME browse load: fetch the first INITIAL_BROWSE_PAGES pages CONCURRENTLY instead of
  // chaining them (the old serial fill was the ~3.3s longest pole — perf phase 3). All pages share
  // the same catalog seed so the per-rail shuffle stays stable, and we merge in PAGE order (not
  // arrival order) and dedupe so rails are deterministic. `page`/`hasMore` are left where infinite
  // scroll can resume. The filtered "see all" grid (selectedTag) keeps plain single-page loading.
  const loadInitialPagesParallel = useCallback(async () => {
    if (loadingCardsRef.current) {
      return;
    }
    loadingCardsRef.current = true;
    setLoading(true);

    const catalogSeed = getOrCreateSessionCatalogSeed();
    const baseParams = {
      limit: ITEMS_PER_PAGE,
      status: 'published',
      ...(catalogSeed ? { seed: catalogSeed } : {}),
    };
    const pageNums = Array.from({ length: INITIAL_BROWSE_PAGES }, (_, i) => i + 1);

    try {
      // Fire every initial page at once (each call is issued synchronously here, before any await),
      // so they overlap on the wire rather than stair-stepping.
      const settled = await Promise.all(
        pageNums.map(async (pageNum) => {
          try {
            const response = await apiClient.getCards({ ...baseParams, page: pageNum });
            return { pageNum, response };
          } catch (error) {
            console.error('Error loading cards page', pageNum, error);
            return { pageNum, response: null };
          }
        })
      );

      const seen = new Set();
      const merged = [];
      let lastPagination = null;

      settled
        .sort((a, b) => a.pageNum - b.pageNum)
        .forEach(({ response }) => {
          if (!response || !response.success) return;
          lastPagination = response.pagination || lastPagination;
          mapCardsToProducts(response.data || []).forEach((product) => {
            if (!seen.has(product.id)) {
              seen.add(product.id);
              merged.push(product);
            }
          });
        });

      setProducts(merged);
      setPage(INITIAL_BROWSE_PAGES);
      // hasNext on the last initial page tells us whether infinite scroll has more to fetch.
      setHasMore(lastPagination ? Boolean(lastPagination.hasNext) : false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingCardsRef.current = false;
    }
  }, [mapCardsToProducts]);

  // Generate or retrieve visitor identifier
  const getVisitorIdentifier = () => {
    let visitorId = localStorage.getItem('visitorId');
    if (!visitorId) {
      // Generate a unique visitor ID based on browser fingerprint
      visitorId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${navigator.userAgent.substring(0, 20)}`;
      localStorage.setItem('visitorId', visitorId);
    }
    return visitorId;
  };

  // Check for obituary ID in URL and load obituary
  useEffect(() => {
    const obituaryId = searchParams.get('id');
    
    if (obituaryId) {
      // Store obituary ID in localStorage
      localStorage.setItem('obituaryId', obituaryId);
      
      // Load obituary data
      const loadObituary = async () => {
        setLoadingObituary(true);
        try {
          const response = await apiClient.getObituary(obituaryId);
          console.log('Obituary API response:', response);
          
          // Handle both response formats: {data: {...}} and {success: true, data: {...}}
          if (response.data) {
            setObituary(response.data);
          } else if (response.success && response.data) {
            setObituary(response.data);
          }

          // Track unique store visit for this obituary
          try {
            const visitorId = getVisitorIdentifier();
            await apiClient.incrementStoreVisits(obituaryId, visitorId);
          } catch (visitError) {
            // Silently fail - don't block the page if visit tracking fails
            console.warn('Failed to increment store visit count:', visitError);
          }
        } catch (error) {
          console.error('Error loading obituary:', error);
          // Try to load from localStorage if API fails
          const cachedObituary = localStorage.getItem('obituary');
          if (cachedObituary) {
            try {
              setObituary(JSON.parse(cachedObituary));
            } catch (e) {
              console.error('Error parsing cached obituary:', e);
            }
          }
        } finally {
          setLoadingObituary(false);
        }
      };
      
      loadObituary();
    } else {
      // Check localStorage for existing obituary ID
      const storedObituaryId = localStorage.getItem('obituaryId');
      if (storedObituaryId) {
        const loadObituary = async () => {
          setLoadingObituary(true);
          try {
            const response = await apiClient.getObituary(storedObituaryId);
            console.log('Obituary API response from storage:', response);
            
            // Handle both response formats: {data: {...}} and {success: true, data: {...}}
            if (response.data) {
              setObituary(response.data);
              // Cache the obituary data
              localStorage.setItem('obituary', JSON.stringify(response.data));
            } else if (response.success && response.data) {
              setObituary(response.data);
              // Cache the obituary data
              localStorage.setItem('obituary', JSON.stringify(response.data));
            }

            // Track unique store visit for this obituary
            try {
              const visitorId = getVisitorIdentifier();
              await apiClient.incrementStoreVisits(storedObituaryId, visitorId);
            } catch (visitError) {
              // Silently fail - don't block the page if visit tracking fails
              console.warn('Failed to increment store visit count:', visitError);
            }
          } catch (error) {
            console.error('Error loading obituary from storage:', error);
            // Try to load from cache
            const cachedObituary = localStorage.getItem('obituary');
            if (cachedObituary) {
              try {
                setObituary(JSON.parse(cachedObituary));
              } catch (e) {
                console.error('Error parsing cached obituary:', e);
              }
            }
          } finally {
            setLoadingObituary(false);
          }
        };
        
        loadObituary();
      }
    }
  }, [searchParams]);

  // Check for tag filter in URL params and trigger load
  useEffect(() => {
    const tagParam = searchParams.get('tag');
    const newTag = tagParam ? tagParam.toLowerCase() : '';
    
    console.log('Tag from URL:', tagParam, 'Normalized:', newTag);
    
    // Always set the tag (even if it's empty)
    setSelectedTag(newTag);
    setQuickViewCard(null);
    
    // Reset and reload when tag changes (including initial mount)
    setProducts([]);
    setPage(1);
    setHasMore(true);
    loadingCardsRef.current = false; // Reset loading flag to allow reload

    if (newTag) {
      // Filtered "see all" grid: single page + infinite scroll (unchanged).
      console.log('Calling loadCards with tag:', newTag);
      loadCards(1, false, newTag); // Pass tag explicitly to avoid closure issues
    } else {
      // Home browse: load the initial pages concurrently (perf phase 3).
      loadInitialPagesParallel();
    }
  }, [searchParams, loadCards, loadInitialPagesParallel]);

  // Filtered-grid-only infinite scroll: when on /?tag=<id>, watch a bottom sentinel and request the
  // next tagged page as it nears the viewport. Home rails are intentionally unaffected.
  useEffect(() => {
    if (!selectedTag) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const currentTarget = observerTarget.current;
    if (!currentTarget) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && hasMore && !loadingMore && !loading) {
          loadMoreCards();
        }
      },
      { rootMargin: '400px', threshold: 0.1 }
    );

    observer.observe(currentTarget);

    return () => observer.disconnect();
  }, [selectedTag, hasMore, loadingMore, loading, loadMoreCards, filteredProducts.length]);

  // Cache obituary data when it changes
  useEffect(() => {
    if (obituary) {
      localStorage.setItem('obituary', JSON.stringify(obituary));
    }
  }, [obituary]);

  // Fetch the live taxonomy (active, ordered categories). Falls back to the built-in default so the
  // home never breaks if the read fails.
  useEffect(() => {
    let cancelled = false;
    const fetchTaxonomy = async () => {
      try {
        if (typeof apiClient.getTaxonomy !== 'function') return;
        const res = await apiClient.getTaxonomy();
        const cats = res?.data?.categories;
        if (!cancelled && Array.isArray(cats) && cats.length > 0) {
          setTaxonomy(cats);
        }
      } catch (error) {
        // Keep the default taxonomy.
      }
    };
    fetchTaxonomy();
    return () => {
      cancelled = true;
    };
  }, []);

  // (Removed) The home rails used to be filled by chaining page loads serially up front; that serial
  // stair-step (page 1→2→3) was the ~3.3s longest pole. It's replaced by loadInitialPagesParallel,
  // which fetches the initial pages concurrently. Infinite scroll handles everything past that set.

  // Filter products based on search and filters
  useEffect(() => {
    if (!products || products.length === 0) {
      setFilteredProducts([]);
      return;
    }
    
    let filtered = products.filter(product => {
      if (!product.name) return false;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
      const matchesOccasions = selectedOccasions.length === 0 || selectedOccasions.includes(product.occasion);
      const matchesStyles = selectedStyles.length === 0 || selectedStyles.includes(product.style);
      // Tag filtering is now done server-side, so no need to filter here
      
      return matchesSearch && matchesPrice && matchesOccasions && matchesStyles;
    });

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        // Mock sorting by newest (in real app, use creation date)
        filtered.sort((a, b) => b.id.localeCompare(a.id));
        break;
      default:
        // Keep original order for 'featured'
        break;
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, priceRange, selectedOccasions, selectedStyles, sortBy]);

  const handleAddToCart = (productId) => {
    setCartCount(prev => prev + 1);
    // In a real app, this would call the API
    console.log('Added to cart:', productId);
  };

  const handleToggleFavorite = (productId) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, isFavorite: !product.isFavorite }
        : product
    ));
  };

  const handlePersonalize = useCallback(() => {
    if (!quickViewCard) return;
    setQuickViewCard(null);
    navigate(`/personalize/${quickViewCard.cardId}`, {
      state: {
        ...CARD_NAV_STATE,
        coverImageId: quickViewCard.imageId,
        coverImageUrl: quickViewCard.coverImageUrl,
      },
    });
  }, [navigate, quickViewCard]);

  const handleCreateLikeThis = useCallback(() => {
    if (!quickViewCard) return;
    setQuickViewCard(null);
    navigate(`/create?like=${encodeURIComponent(quickViewCard.cardId)}`, {
      state: { likeCoverImageUrl: quickViewCard.coverImageUrl, likeName: quickViewCard.name },
    });
  }, [navigate, quickViewCard]);

  const handleCardClick = useCallback((product) => {
    if (!product) return;
    setQuickViewCard({
      cardId: product.id,
      imageId: product.coverImageId,
      coverImageUrl: product.coverImageUrl,
      name: product.name,
      aiReferenceBlocked: !!product.aiReferenceBlocked,
    });
  }, []);

  const currentProducts = filteredProducts;

  // Group the loaded cards into one rail per non-empty category (home browse, no tag filter).
  // Rails are built from each card's structured `categories` (multi-label) — a card appears in every
  // matching rail, an uncategorized card appears in none. Each rail is ordered with a per-visit
  // seeded shuffle (stable within the session, varying between visits, independent per rail).
  const railsByCategory = useMemo(
    () => buildCategoryRails(filteredProducts, taxonomy, getOrCreateSessionCatalogSeed() || 'rails'),
    [filteredProducts, taxonomy]
  );

  // Three real covers for the invitation's fan — picked with the SAME per-session seed the rails
  // use, so the set is stable while browsing and different on the next visit.
  //
  // Cards WITH a resolvable cover come first, but the fan is topped up from the rest of the
  // catalogue rather than shrinking: an environment whose cards carry no cover yet (every local
  // dev seed) would otherwise drop the fan entirely and leave the invitation as words on a slab.
  // The rails already render those same cards on <CardCoverImage>'s placeholder, so the fan is
  // consistent with them; in production every published card carries a cover, so the top-up is
  // never reached. The fan only disappears when the catalogue itself is empty.
  const inviteFanCards = useMemo(() => {
    const seed = `${getOrCreateSessionCatalogSeed() || 'rails'}:invite`;
    const shuffled = seededShuffle(products, seed);
    const withCover = shuffled.filter((p) => p.coverImageUrl || p.coverImageId);
    const withoutCover = shuffled.filter((p) => !(p.coverImageUrl || p.coverImageId));
    return [...withCover, ...withoutCover].slice(0, 3);
  }, [products]);

  // Saved designs are local to the device (localStorage), so this is read once on mount rather than
  // subscribed — nothing else on this page can change the count while it is open.
  const [savedDesignCount, setSavedDesignCount] = useState(0);
  useEffect(() => {
    try {
      setSavedDesignCount(getCreations().length);
    } catch {
      setSavedDesignCount(0);
    }
  }, []);

  return (
    <>
      {/* Print styles for Safari PDF export */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            * {
              transform-style: flat !important;
            }
            [style*="rotateY"] {
              transform: none !important;
            }
            [style*="preserve-3d"] {
              transform-style: flat !important;
            }
          }
        `
      }} />
      {/* Header */}
      <StoreHeader cartCount={cartCount} />

      {/* Full-bleed hero — rotating carousel of finished banners (whole, never cropped) */}
      <HeroCarousel />

      {/* Obituary personalization — preserved as a slim band directly under the hero image */}
      {obituary && (
        <Box
          sx={{
            backgroundColor: '#FEFDF6',
            borderBottom: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)',
            py: { xs: 1.5, md: 2 },
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, md: 2.5 },
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              {(obituary.photoUrl || obituary.photoId) && (
                <Box
                  component="img"
                  src={apiClient.getObituaryPhotoUrl(obituary.id)}
                  alt={obituary.deceasedName || 'Obituary photo'}
                  sx={{
                    width: { xs: 56, md: 64 },
                    height: { xs: 56, md: 64 },
                    objectFit: 'cover',
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    flex: '0 0 auto',
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                {obituary.deceasedName && (
                  <Typography
                    sx={{
                      fontSize: { xs: '1.05rem', md: '1.25rem' },
                      fontWeight: 700,
                      color: 'black',
                      lineHeight: 1.2,
                    }}
                  >
                    In memory of {obituary.deceasedName}
                  </Typography>
                )}
                {obituary.dateOfDeath && (
                  <Typography
                    sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 500, color: '#206884' }}
                  >
                    {formatDateOfDeath(obituary.dateOfDeath)}
                  </Typography>
                )}
                {obituary.family && Array.isArray(obituary.family) && obituary.family.length > 0 && (
                  <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, color: '#555', mt: 0.25 }}>
                    For {obituary.family
                      .map(member => member.name || [member.firstName, member.lastName].filter(Boolean).join(' '))
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
                <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, color: '#206884', mt: 0.5 }}>
                  Choose a card to express your condolences and support to the family.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* The invitation — home only. The rails merchandise what already exists; this is the only
          place that says a card can be made that does not. Sits above the first rail so it is met
          before the browsing habit takes over, and below the obituary band so the memorial context
          still comes first. */}
      {!selectedTag && (
        <Container maxWidth="xl" sx={{ pt: 2 }}>
          <InviteBand fanCards={inviteFanCards} />
          <SavedDesignsRow count={savedDesignCount} />
        </Container>
      )}

      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 3 },
          // Reserve the thumb-zone bar's height on phones so the last rail is never parked under it.
          pb: { xs: `${MOBILE_ACTION_BAR_PAD}px`, md: 3 },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !selectedTag ? (
          // Home browse — Netflix-style horizontal rails grouped by category
          railsByCategory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No cards available right now
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check back soon.
              </Typography>
            </Box>
          ) : (
            <Box>
              {railsByCategory.map((rail, index) => (
                <CategoryRail
                  key={rail.category.id}
                  category={rail.category}
                  products={rail.items}
                  eager={index < EAGER_RAILS}
                />
              ))}
            </Box>
          )
        ) : (
          // Filtered "see all" view for /?tag=<id> — the existing grid
          <Box sx={{ width: '100%' }}>
            {/* Results Count */}
            {!loading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredProducts.length === products.length 
                    ? `Showing ${filteredProducts.length} ${filteredProducts.length === 1 ? 'card' : 'cards'}`
                    : `Showing ${filteredProducts.length} of ${products.length} ${products.length === 1 ? 'card' : 'cards'}`
                  }
                </Typography>
              </Box>
            )}

            {/* Products Grid */}
            <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {currentProducts.map((product) => {
                return (
                  <Box
                    key={product.id}
                    data-testid={`filtered-grid-card-${product.id}`}
                    onClick={() => handleCardClick(product)}
                    sx={{
                      position: 'relative',
                      flex: '1 1 auto',
                      minWidth: { xs: 'calc((100% - 24px) / 2)', sm: 200 },
                      maxWidth: { xs: 'calc((100% - 24px) / 2)', sm: 'calc((100% - 96px) / 5)' },
                      height: 'auto',
                      cursor: 'pointer',
                    }}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        height: '100%',
                        padding: 0,
                        overflow: 'hidden',
                        backgroundColor: 'grey.100',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardCoverImage
                        cardId={product.id}
                        imageId={product.coverImageId}
                        coverImageUrl={product.coverImageUrl}
                        alt={product.name}
                      />
                      {product.originalPrice > product.price && (
                        <Chip
                          label="Sale"
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                          }}
                        />
                      )}
                    </Card>
                  </Box>
                );
              })}
            </Box>

            {currentProducts.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No cards found matching your criteria
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search or filters
                </Typography>
              </Box>
            )}

            {/* Infinite scroll trigger */}
            {hasMore && (
              <Box
                ref={observerTarget}
                data-testid="filtered-grid-sentinel"
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  minHeight: 100,
                }}
              >
                {loadingMore && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more cards...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* End of results message */}
            {!hasMore && currentProducts.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  You've reached the end of the results
                </Typography>
              </Box>
            )}
            <CardQuickViewModal
              open={Boolean(quickViewCard)}
              onClose={() => setQuickViewCard(null)}
              cardId={quickViewCard?.cardId}
              imageId={quickViewCard?.imageId}
              coverImageUrl={quickViewCard?.coverImageUrl}
              alt={quickViewCard?.name || ''}
              ariaLabel={quickViewCard ? `${quickViewCard.name} preview` : 'Card preview'}
              {...(quickViewCard?.aiReferenceBlocked
                ? {}
                : { primaryAction: { label: 'Create one like this', onClick: handleCreateLikeThis } })}
              actionLabel="Use this exact card"
              onConfirm={handlePersonalize}
            />
          </Box>
        )}
      </Container>

      {/* Phone-only: the revised header carries no action, so the primary one lives in the thumb
          zone and follows the shopper down the rails. */}
      <StoreMobileActionBar />
    </>
  );
};

export default StorePage;
